import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Select, UnstyledButton } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { notifications } from '@mantine/notifications';
import type { ReportView, ReportSessionDate, ReportSessionOption } from 'shared-models/browser';
import { Button } from '../../../../components/Button';
import navyModal from '../../../../components/Modal/navy-modal.module.scss';
import { reportsService } from '../../../../services/reports/reports.service';
import { employeesService } from '../../../../services/employees/employees.service';
import { printersService } from '../../../../services/printers/printers.service';
import type { PrinterView } from '../../../../services/printers/printers.service';
import { toHebrewApiErrorMessage } from '../../../../services/index/index.service';
import { captureReceiptSnapshot } from '../receipt/shared/receipt.utils';
import { PosReportView } from './PosReportView';
import { PosSessionPickerModal } from './PosSessionPickerModal';
import { PosWheelDatePicker } from './PosWheelDatePicker';
import type { WheelDate } from './PosWheelDatePicker';
import { resolveReceiptAutoPrinterId, resolveReceiptPrintTarget } from '../../hooks/fixed-printer.util';
import classes from './pos-report-range-modal.module.scss';

const REPORT_PRINTER_STORAGE_KEY = 'posReportPrinterId';

export type PosReportRangeMode = 'restoreZ' | 'worker';

export type PosReportRangeModalProps = {
  mode: PosReportRangeMode | null;
  onClose: () => void;
  tenantId: string;
  employeeSession: string;
  onReportReady: (report: ReportView, title: string) => void;
};

type WizardStep = 0 | 1 | 2;

// 'YYYY-MM-DD' → wheel tuple; restore-Z picks the shift's opening day, worker its end day.
const parseDate = (date: string): WheelDate => {
  const [year, month, day] = date.split('-').map(Number);
  return { year, month, day };
};

const toEpoch = (d: WheelDate) => Date.UTC(d.year, d.month - 1, d.day);

const pad = (n: number) => String(n).padStart(2, '0');
const formatPickerDate = (d: WheelDate) => `${d.year}-${pad(d.month)}-${pad(d.day)}`;

// Summary: Restore-Z / worker wizard — 3 steps over a wheel picker, builds + prints a report.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-reports
const PosReportRangeModalComponent: React.FC<PosReportRangeModalProps> = ({ mode, onClose, tenantId, employeeSession, onReportReady }) => {
  const title = mode === 'worker' ? 'דוח עובד' : 'הדפס מחדש דוח Z לפי תאריך';
  const [step, setStep] = useState<WizardStep>(0);
  const [dates, setDates] = useState<ReportSessionDate[]>([]);
  const [endDate, setEndDate] = useState<WheelDate | null>(null);
  const [employees, setEmployees] = useState<{ value: string; label: string }[]>([]);
  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [pickerSessions, setPickerSessions] = useState<ReportSessionOption[] | null>(null);
  const [report, setReport] = useState<ReportView | null>(null);
  const [printers, setPrinters] = useState<PrinterView[]>([]);
  const [selectedPrinterId, setSelectedPrinterId] = useState<string | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);

  const captureRef = useRef<HTMLDivElement>(null);
  const printInFlightRef = useRef(false);

  // Allowed dates = closed-session days from the lightweight wizard-open payload.
  const allowed = useMemo<WheelDate[]>(() => dates.map((d) => parseDate(d.date)), [dates]);

  useEffect(() => {
    if (!mode) {
      return;
    }
    setStep(0); setEndDate(null); setEmployeeId(null); setPickerSessions(null);
    setReport(null); setGenerating(false);
    setSelectedPrinterId(localStorage.getItem(REPORT_PRINTER_STORAGE_KEY));
    void reportsService.listSessionDates(tenantId, employeeSession, mode === 'worker', mode === 'restoreZ').then(setDates).catch(() => undefined);
    void printersService
      .listActive(tenantId, employeeSession)
      .then((data) => {
        setPrinters(data);
        const autoId = resolveReceiptAutoPrinterId(data);
        if (autoId != null) setSelectedPrinterId(String(autoId));
      })
      .catch(() => undefined);
    if (mode === 'worker') {
      void employeesService
        .listForSettings(tenantId, employeeSession)
        .then((rows) => setEmployees(rows.map((e) => ({ value: String(e.id), label: e.fullName }))))
        .catch(() => undefined);
    }
  }, [mode, tenantId, employeeSession]);

  // Seed the wheel once dates load: latest available day for both restore-Z and worker.
  useEffect(() => {
    if (allowed.length === 0) {
      return;
    }
    const sorted = [...allowed].sort((a, b) => toEpoch(a) - toEpoch(b));
    const latest = sorted[sorted.length - 1];
    setEndDate((prev) => prev ?? latest);
  }, [allowed]);

  // Build + show the report for one specific session, then advance to the print step.
  const buildForSession = useCallback(async (sessionId: number) => {
    setGenerating(true);
    try {
      const reports = await reportsService.getRange(
        tenantId,
        { sessionIds: [sessionId], aggregate: false, employeeId: employeeId ? Number(employeeId) : undefined },
        employeeSession,
      );
      const built = reports[0] ?? null;
      if (!built) {
        notifications.show({ color: 'red', title: 'שגיאה', message: 'לא ניתן להפיק דוח', autoClose: 3000 });
        return;
      }
      setReport(built);
      setStep(1);
    } catch (error: unknown) {
      if (error instanceof Error) {
        notifications.show({ color: 'red', title: 'שגיאה', message: toHebrewApiErrorMessage(error.message), autoClose: 3000 });
      }
    } finally {
      setGenerating(false);
    }
  }, [employeeId, tenantId, employeeSession]);

  // Continue: fetch the picked day's sessions on demand (totals only here) then branch by count.
  const continueToPrint = useCallback(async () => {
    if (generating || !endDate) {
      return;
    }
    if (mode === 'worker' && !employeeId) {
      notifications.show({ color: 'red', title: 'שגיאה', message: 'בחר עובד', autoClose: 3000 });
      return;
    }
    setGenerating(true);
    let dateSessions: ReportSessionOption[];
    try {
      dateSessions = await reportsService.getSessionsForDate(
        tenantId,
        formatPickerDate(endDate),
        employeeSession,
        mode === 'worker' && employeeId ? Number(employeeId) : undefined,
        mode === 'worker',
        mode === 'restoreZ',
      );
    } catch (error: unknown) {
      if (error instanceof Error) {
        notifications.show({ color: 'red', title: 'שגיאה', message: toHebrewApiErrorMessage(error.message), autoClose: 3000 });
      }
      setGenerating(false);
      return;
    }
    if (dateSessions.length === 0) {
      notifications.show({ color: 'red', title: 'שגיאה', message: 'לא נמצאו משמרות לתאריך שנבחר', autoClose: 3000 });
      setGenerating(false);
      return;
    }
    if (dateSessions.length > 1) {
      setPickerSessions(dateSessions);
      setGenerating(false);
      return;
    }
    await buildForSession(dateSessions[0].id);
  }, [generating, endDate, mode, employeeId, tenantId, employeeSession, buildForSession]);

  // A row in the shifts picker was tapped: run the report for that single session.
  const handlePickSession = useCallback((session: ReportSessionOption) => {
    setPickerSessions(null);
    void buildForSession(session.id);
  }, [buildForSession]);

  // Title for the shifts picker — worker mode names the worker, restore-Z is worker-agnostic.
  const pickerTitle = useMemo(() => {
    if (!endDate) return '';
    const dateLabel = formatPickerDate(endDate);
    if (mode === 'worker') {
      const worker = employees.find((e) => e.value === employeeId)?.label ?? '';
      return `משמרות קופה עבור ${worker} בתאריך ${dateLabel}`;
    }
    return `משמרות קופה בתאריך ${dateLabel}`;
  }, [mode, endDate, employees, employeeId]);

  const handlePrint = useCallback(async (printerIdOverride?: string) => {
    const printerId = printerIdOverride ?? selectedPrinterId;
    if (printInFlightRef.current || !printerId || !captureRef.current || !report) {
      return;
    }
    try {
      printInFlightRef.current = true;
      setIsPrinting(true);
      const image = await captureReceiptSnapshot(captureRef.current);
      if (!image) {
        notifications.show({ color: 'red', title: 'שגיאה', message: 'לא ניתן ליצור תמונה להדפסה', autoClose: 3000 });
        return;
      }
      await printersService.printReport(tenantId, Number(printerId), image, employeeSession);
      localStorage.setItem(REPORT_PRINTER_STORAGE_KEY, printerId);
      setStep(2);
    } catch (error: unknown) {
      notifications.show({
        color: 'red',
        title: 'ההדפסה נכשלה',
        message: error instanceof Error ? toHebrewApiErrorMessage(error.message) : 'שגיאה לא ידועה',
        autoClose: 3000,
      });
    } finally {
      printInFlightRef.current = false;
      setIsPrinting(false);
    }
  }, [selectedPrinterId, report, tenantId, employeeSession]);

  const autoId = resolveReceiptAutoPrinterId(printers);
  const autoValid = autoId != null;
  const receiptTarget = resolveReceiptPrintTarget(printers);
  const printerChoices = receiptTarget.kind === 'pick' && receiptTarget.candidates ? receiptTarget.candidates : printers;

  const steps = ['שאלון', 'הדפסה', 'סיום'];

  return (
    <Modal
      opened={mode !== null}
      onClose={onClose}
      title={<strong className={classes.modalTitle}>{title}</strong>}
      size="md"
      centered
      radius="md"
      dir="rtl"
      padding={0}
      closeButtonProps={{ 'data-testid': 'report-range-close' } as Record<string, string>}
      classNames={{ root: navyModal.root, content: classes.modalContent, header: classes.modalHeader, body: classes.modalBody }}
    >
      <div className={classes.stack}>
        <div className={classes.steps}>
          {steps.map((label, index) => (
            <React.Fragment key={label}>
              <div className={classes.stepNode}>
                <span
                  className={[
                    classes.stepCircle,
                    index === step ? classes.stepCircleActive : '',
                    index < step ? classes.stepCircleDone : '',
                  ].filter(Boolean).join(' ')}
                >
                  {index < step ? <FontAwesomeIcon icon={faCheck as IconProp} /> : index + 1}
                </span>
                <span className={classes.stepLabel}>{label}</span>
              </div>
              {index < steps.length - 1 && <span className={classes.stepLine} />}
            </React.Fragment>
          ))}
        </div>

        {step === 0 && (
          <div className={classes.questionnaire}>
            {mode === 'worker' && (
              <Select data-testid="report-range-employee" label="עובד" placeholder="בחר עובד" data={employees} value={employeeId} onChange={setEmployeeId} />
            )}
            <div className={classes.wheelLabel}>{mode === 'worker' ? 'תאריך סיום' : 'תאריך פתיחה'}</div>
            <PosWheelDatePicker allowed={allowed} value={endDate} onChange={setEndDate} />
            <Button variant="save" fullWidth onClick={() => { void continueToPrint(); }} disabled={generating || allowed.length === 0} dataTestId="report-range-continue">
              {generating ? 'מפיק...' : 'המשך'}
            </Button>
          </div>
        )}

        {step === 1 && (
          <div className={classes.printStep}>
            <div className={classes.printHeader}>
              {!autoValid && <span className={classes.printTitle}>רשימת מדפסות</span>}
              <UnstyledButton
                component="button"
                type="button"
                data-testid="report-range-preview"
                className={classes.previewButton}
                onClick={() => { if (report) onReportReady(report, title); }}
                disabled={!report}
              >
                תצוגה מקדימה
              </UnstyledButton>
            </div>
            {!autoValid && (
              <div className={classes.printerList}>
                {printerChoices.length === 0 ? (
                  <span className={classes.empty}>אין מדפסות פעילות</span>
                ) : (
                  printerChoices.map((printer) => {
                    return (
                      <UnstyledButton
                        key={printer.id}
                        component="button"
                        type="button"
                        data-testid={`report-range-printer-option-${printer.id}`}
                        className={classes.printerItem}
                        onClick={() => { void handlePrint(String(printer.id)); }}
                        disabled={isPrinting}
                      >
                        <span>{printer.name}</span>
                      </UnstyledButton>
                    );
                  })
                )}
              </div>
            )}
            <div className={classes.actions}>
              <Button variant="cancel" onClick={() => setStep(0)} disabled={isPrinting} dataTestId="report-range-back">
                חזור
              </Button>
              {autoValid && (
                <Button
                  variant="save"
                  onClick={() => { void handlePrint(String(autoId)); }}
                  disabled={isPrinting}
                  dataTestId="report-range-print"
                >
                  {isPrinting ? 'מדפיס...' : 'הדפס'}
                </Button>
              )}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className={classes.doneStep}>
            <span className={classes.doneIcon}><FontAwesomeIcon icon={faCheck as IconProp} /></span>
            <span className={classes.doneText}>הדוח נשלח להדפסה</span>
            <Button variant="save" fullWidth onClick={onClose} dataTestId="report-range-done">סיום</Button>
          </div>
        )}
      </div>

      {report && (
        <div className={classes.offscreen} aria-hidden="true">
          <PosReportView report={report} variant="print" innerRef={captureRef} />
        </div>
      )}

      <PosSessionPickerModal
        opened={pickerSessions !== null}
        title={pickerTitle}
        sessions={pickerSessions ?? []}
        onPick={handlePickSession}
        onClose={() => setPickerSessions(null)}
      />
    </Modal>
  );
};

export const PosReportRangeModal = React.memo(PosReportRangeModalComponent);
